import { useChartMarkers } from '@/modules/imports/container/scan-discs-popup-container/editor/useChartMarkers'
import { parseChartIso } from '@/modules/imports/components/TachographChartCanvas'
import React from 'react'
import { DiscCanvas, ZOOM_MAX, ZOOM_MIN } from './DiscCanvas'
import DiscViewerActionButtons from './DiscViewerActionButtons'
import { ChartEditor } from '@/modules/imports/container/scan-discs-popup-container/editor/useChartEditor'
import { useChartEditorShortcuts } from '@/modules/imports/container/scan-discs-popup-container/editor/useChartEditorShortcuts'
import { ChartActivityModeButtons } from '@/modules/imports/container/scan-discs-popup-container/editor/ChartActivityModeButtons'
import {
  ActivityTypeId,
  ChartScanDisc,
  fetchDiscCanonicalImageObjectUrl,
} from '@/lib/api/company/chartScan'
import { LoaderIcon } from '@/app/features/icons/Loader'

const DiscViewer = ({
  companyId,
  disc,
  editor,
  onContextMenu,
  menuInteracting,
  onContextMenuVehicleChange,
  onMoveVehicleChange,
}: {
  companyId: string
  disc: ChartScanDisc
  editor: ChartEditor
  onContextMenu: (
    activity: ChartScanDisc['activities'][number] | null,
    position: { x: number; y: number },
    minute?: number,
  ) => void
  menuInteracting: boolean
  onContextMenuVehicleChange: (id: number, position: { x: number; y: number }) => void
  onMoveVehicleChange: (id: number, minute: number) => void
}) => {
  const [zoom, setZoom] = React.useState(1)
  const [showDistance, setShowDistance] = React.useState(false)
  const [addingMode, setAddingMode] = React.useState<ActivityTypeId | null>(
    null,
  )

  useChartEditorShortcuts(!menuInteracting, {
    addingMode,
    onSetMode: setAddingMode,
    onDeleteSelected: editor.deleteSelected,
    onDeleteAllOfType: editor.deleteAllOfType,
    onUndo: editor.undo,
    onRedo: editor.redo,
  })

  const [compactView, setCompactView] = React.useState(true)
  const [imageUrl, setImageUrl] = React.useState<string | null>(null)
  React.useEffect(() => {
    let revoked = false
    let url: string | null = null
    setImageUrl(null)
    fetchDiscCanonicalImageObjectUrl(companyId, disc.discId).then((u) => {
      if (revoked) URL.revokeObjectURL(u)
      else {
        url = u
        setImageUrl(u)
      }
    })
    return () => {
      revoked = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [companyId, disc.discId])

  const canvasWrapRef = React.useRef<HTMLDivElement>(null)
  const [canvasSize, setCanvasSize] = React.useState(700)
  React.useEffect(() => {
    if (!canvasWrapRef.current) return undefined
    const ro = new ResizeObserver((entries) => {
      const e = entries[0]
      if (!e) return
      const { width, height } = e.contentRect
      setCanvasSize(Math.max(0, Math.floor(Math.min(width, height))))
    })
    ro.observe(canvasWrapRef.current)
    return () => ro.disconnect()
  }, [])

  const markers = useChartMarkers(companyId, editor)

  const dayStartMin = isoToDayMinute(editor.dayStartDate)
  const dayEndMin = isoToDayMinute(editor.dayEndDate)

  const ZOOM_STEP = 0.5
  const handleZoomIn = () =>
    setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))
  const handleZoomOut = () =>
    setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))

  return (
    <div
      ref={canvasWrapRef}
      className="tw-relative tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-border tw-border-base-200 tw-bg-white"
    >
      <div className="tw-absolute tw-left-5 tw-top-5 tw-z-[100]">
        <ChartActivityModeButtons
          addingMode={addingMode}
          onChangeMode={setAddingMode}
        />
      </div>
      <DiscViewerActionButtons
        zoom={zoom}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        canUndo={editor.canUndo}
        canRedo={editor.canRedo}
        onUndo={editor.undo}
        onRedo={editor.redo}
        compactView={compactView}
        onToggleCompactView={() => setCompactView((v) => !v)}
        showDistance={showDistance}
        onToggleDistance={() => setShowDistance((v) => !v)}
      />
      {!imageUrl ? (
        <div className="tw-text-sm tw-text-slate-400">
          <LoaderIcon className="tw-animate-spin" />
        </div>
      ) : (
        <DiscCanvas
          imageUrl={imageUrl}
          disc={disc}
          activities={editor.activities}
          onActivitiesChange={editor.replaceActivities}
          hoveredActivityId={editor.hoveredActivityId}
          selectedActivityIds={editor.selectedActivityIds}
          onHoverActivity={(id, fromCanvas) =>
            editor.setHovered(id, fromCanvas)
          }
          onSelectActivity={(ids, shift, ctrl) => {
            if (shift || ctrl) {
              const next = new Set(editor.selectedActivityIds)
              for (const id of ids) {
                if (next.has(id)) next.delete(id)
                else next.add(id)
              }
              editor.setSelected(Array.from(next))
            } else {
              editor.setSelected(ids)
            }
          }}
          onContextMenuActivity={onContextMenu}
          startBoundaryMinute={dayStartMin}
          endBoundaryMinute={dayEndMin}
          noonShiftMinutes={editor.noonShiftMinutes}
          onChangeStartBoundary={(min) =>
            editor.setDayStartTime(minToHHMM(min))
          }
          onChangeEndBoundary={(min) => editor.setDayEndTime(minToHHMM(min))}
          onChangeNoonShift={editor.setNoonShiftMinutes}
          onClickEmptyArea={(min) => {
            if (addingMode != null) editor.addActivityAtMinute(addingMode, min)
          }}
          addingMode={addingMode != null}
          beginCountryCode={markers.beginCountryCode}
          endCountryCode={markers.endCountryCode}
          distanceData={editor.distanceData}
          showDistance={showDistance}
          onAddDistancePoint={(kind, p) => editor.addDistancePoint(kind, p)}
          onRemoveDistancePoint={(kind, i) =>
            editor.removeDistancePoint(kind, i)
          }
          compactView={compactView}
          vehicleChanges={markers.vehicleChanges}
          onMoveVehicleChange={onMoveVehicleChange}
          onContextMenuVehicleChange={onContextMenuVehicleChange}
          countryChanges={markers.countryChanges}
          width={canvasSize}
          height={canvasSize}
          zoom={zoom}
          onZoomChange={setZoom}
        />
      )}
    </div>
  )
}

export default DiscViewer

function isoToDayMinute(iso: string): number {
  const d = parseChartIso(iso)
  return d.getUTCHours() * 60 + d.getUTCMinutes()
}
function minToHHMM(min: number): string {
  const m = ((min % 1440) + 1440) % 1440
  const h = Math.floor(m / 60)
  return `${String(h).padStart(2, '0')}:${String(m - h * 60).padStart(2, '0')}`
}
